/**
 * Table row-density preference (Compact ↔ Comfortable).
 *
 * A single browser-local preference that toggles how tightly the app's
 * ledger/order tables (anything using the `.ledger-table` class) are packed.
 * We drive it purely with a `data-density` attribute on the <html> element so
 * the switch is instant and CSS-only — no re-render of the tables themselves.
 *
 * "comfortable" is the default; "compact" opts into tighter padding + type.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'inventique.tableDensity';
const VALID = ['comfortable', 'compact'];
export const DEFAULT_DENSITY = 'comfortable';

export function getInitialDensity() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (VALID.includes(v)) return v;
  } catch {
    /* localStorage unavailable — fall back to the default */
  }
  return DEFAULT_DENSITY;
}

export function applyDensity(value) {
  const v = VALID.includes(value) ? value : DEFAULT_DENSITY;
  try {
    document.documentElement.setAttribute('data-density', v);
  } catch {
    /* SSR / no document — safe to ignore */
  }
}

// Apply the saved preference at import time so tables render at the right
// density on the very first paint (no flash of comfortable → compact).
applyDensity(getInitialDensity());

/**
 * React hook exposing the current density plus setters. Keeps the <html>
 * attribute and localStorage in sync whenever the value changes.
 */
export function useDensity() {
  const [density, setDensityState] = useState(getInitialDensity);

  useEffect(() => {
    applyDensity(density);
    try {
      localStorage.setItem(STORAGE_KEY, density);
    } catch {
      /* ignore persistence failures */
    }
  }, [density]);

  const setDensity = useCallback((v) => {
    setDensityState(VALID.includes(v) ? v : DEFAULT_DENSITY);
  }, []);

  const toggleDensity = useCallback(() => {
    setDensityState((d) => (d === 'compact' ? 'comfortable' : 'compact'));
  }, []);

  return { density, setDensity, toggleDensity, isCompact: density === 'compact' };
}
